/* eslint-disable react/jsx-props-no-spreading */
import React, { useMemo, useCallback, MouseEvent, forwardRef } from 'react';
import {
  GDSIconSource,
  GDSStyleObject,
  GDSTextActionIndicator,
  GDSTextActionSize,
} from '@leagueplatform/genesis-core';
import { MasonryDriverAction } from '#types/masonry-driver-node-actions.ts';
import { MasonryTextAction as MTextAction } from '#common/masonry-text-action/masonry-text-action.component.tsx';
import { useInvokeAction } from '../../../hooks/use-invoke-action.hook';

export type MasonryTextActionVariant = 'default' | 'destructive' | 'subtle';

export type MasonryTextActionProperties = {
  text?: string;
  icon?: GDSIconSource;
  indicator?: GDSTextActionIndicator;
  size?: GDSTextActionSize;
  onClick?: MasonryDriverAction;
  variant?: MasonryTextActionVariant;
  className?: string;
  loading?: boolean;
  css?: GDSStyleObject;
};

export const MasonryTextAction = forwardRef<
  HTMLAnchorElement,
  MasonryTextActionProperties
>(
  (
    {
      icon,
      indicator,
      onClick,
      size,
      text,
      variant = 'default',
      className,
      loading,
      css,
      ...props
    },
    ref,
  ) => {
    const invokeAction = useInvokeAction();

    const handleOnClick = useCallback(
      (event: MouseEvent) => {
        if (onClick) {
          invokeAction({
            event,
            ...onClick,
          });
        }
      },
      [invokeAction, onClick],
    );

    const variantProps = useMemo(() => {
      switch (variant) {
        case 'default':
          return { emphasis: 'base' } as const;
        case 'subtle':
          return { emphasis: 'subtle' } as const;
        case 'destructive':
          return { destructive: true } as const;
        default:
          return undefined;
      }
    }, [variant]);

    return (
      <MTextAction
        ref={ref}
        action={onClick}
        icon={icon}
        indicator={indicator}
        onClick={handleOnClick}
        size={size}
        className={className}
        loading={loading}
        css={css}
        {...variantProps}
        {...props}
      >
        {text}
      </MTextAction>
    );
  },
);

// Adding a display name here because some components use the display name to identify the component type and only allow TextAction in certain slots.
MasonryTextAction.displayName = 'MasonryTextAction';
